import { Box, List, ListItemButton, Typography } from '@mui/material';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getNewAddedItemsTotalByCustomListId } from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import {
    CUSTOM_LIST_NAV_NAME_CLASS,
    customListNavColors,
    customListNavNameColor,
    customListNavRowBackground,
    customListNavRowHoverSx,
} from 'shared/ui/CustomListNavRow';
import { InfoHintIcon } from 'shared/ui/Tooltip';

import type { CustomList } from 'entities/CustomList';

interface OrderAddItemsListsRailProps {
    lists: CustomList[];
    activeListId?: string;
    onSelectList: (listId: string) => void;
}

export const OrderAddItemsListsRail = memo(
    ({ lists, activeListId, onSelectList }: OrderAddItemsListsRailProps) => {
        const { t } = useTranslation();
        const addedByListId = useSelector(getNewAddedItemsTotalByCustomListId);

        const sortedLists = useMemo(
            () =>
                [...lists].sort((a, b) =>
                    a.name.localeCompare(b.name, undefined, {
                        sensitivity: 'base',
                    }),
                ),
            [lists],
        );

        return (
            <Box
                sx={{
                    width: '280px',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    bgcolor: 'background.paper',
                    overflow: 'hidden',
                    borderRadius: '12px',
                }}
                data-testclass="order-add-items-lists-rail"
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '16px 16px 8px',
                        flexShrink: 0,
                    }}
                >
                    <Typography variant="subtitle2">
                        {t('customLists.lists')}
                    </Typography>
                    <InfoHintIcon
                        content={t('customLists.listsInfoTooltip')}
                        dataTestClass="order-add-items-lists-info"
                    />
                </Box>
                <List
                    disablePadding
                    sx={{
                        overflow: 'auto',
                        flex: 1,
                        minHeight: 0,
                    }}
                >
                    {sortedLists.map((list) => {
                        const isEmpty = list.itemCount === 0;
                        const isActive = list.id === activeListId;
                        const hasItemsInOrder =
                            (addedByListId[list.id] || 0) > 0;
                        const rowState = {
                            isActive,
                            isDisabled: isEmpty,
                            hasItemsInOrder,
                        };

                        return (
                            <ListItemButton
                                key={list.id}
                                disabled={isEmpty}
                                selected={isActive}
                                onClick={() => {
                                    if (!isEmpty) {
                                        onSelectList(list.id);
                                    }
                                }}
                                data-testclass="order-add-items-list-rail-row"
                                sx={{
                                    borderRadius: 0,
                                    paddingTop: '9px',
                                    paddingBottom: '9px',
                                    paddingRight: '16px',
                                    paddingLeft: '16px',
                                    backgroundColor:
                                        customListNavRowBackground(rowState),
                                    borderLeft: '2px solid',
                                    borderColor: isActive
                                        ? customListNavColors.active
                                        : 'transparent',
                                    '&:hover': isEmpty
                                        ? { backgroundColor: 'transparent' }
                                        : customListNavRowHoverSx,
                                    '&.Mui-selected, &.Mui-selected:hover': {
                                        backgroundColor:
                                            customListNavColors.background,
                                    },
                                    '&.Mui-disabled': {
                                        opacity: 1,
                                        backgroundColor: 'transparent',
                                    },
                                }}
                            >
                                <Typography
                                    className={CUSTOM_LIST_NAV_NAME_CLASS}
                                    variant="subtitle2"
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        color: customListNavNameColor(rowState),
                                    }}
                                    noWrap
                                >
                                    {list.name}
                                </Typography>
                            </ListItemButton>
                        );
                    })}
                </List>
            </Box>
        );
    },
);

OrderAddItemsListsRail.displayName = 'OrderAddItemsListsRail';
